import React from "react";
import { Loader2, RefreshCw, Star } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { apiDaftarUlasan, formatApiErrorDetail } from "@/lib/api";
import { rataTeks } from "@/lib/ulasan";
import * as W from "@/lib/waktu";

// Owner 2026-10-16 (requirement 60): ratings sent by customers from their
// digital nota. Owner and Manager only (checked by the server), read only.
const JENIS = { DIGITAL_PRINT: "Digital Print", JERSEY: "Jersey", DTF: "DTF", CUSTOM: "Custom" };
const inputCls = "rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100";
const Bintang = ({ nilai }) => (
  <span className="inline-flex" aria-label={`${nilai} bintang`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} className={i <= nilai ? "fill-amber-400 text-amber-400" : "text-slate-300"} />)}
  </span>
);

export default function MasukanCustomer() {
  const [f, setF] = React.useState({ dari: "", sampai: "", bintang: "", jenis: "", cs: "" });
  const [d, setD] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      setD(await apiDaftarUlasan(params)); setError(null);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setLoading(false); }
  }, [f]);
  React.useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="masukan-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="mx-auto max-w-6xl space-y-5 px-4 py-6 sm:px-6 lg:px-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">Masukan Customer</h1>
          <p className="mt-1 text-sm text-slate-400">Penilaian dari link nota setelah pesanan diambil/dikirim. Hanya dibaca Owner dan Manager; tidak pernah tampil di halaman customer lain, TV, atau tracking.</p>
        </div>
        <div data-testid="masukan-saring" className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-400">Dari <input data-testid="masukan-dari" type="date" value={f.dari} onChange={(e) => setF({ ...f, dari: e.target.value })} className={`${inputCls} ml-1`} /></label>
          <label className="text-xs text-slate-400">Sampai <input data-testid="masukan-sampai" type="date" value={f.sampai} onChange={(e) => setF({ ...f, sampai: e.target.value })} className={`${inputCls} ml-1`} /></label>
          <select data-testid="masukan-bintang" value={f.bintang} onChange={(e) => setF({ ...f, bintang: e.target.value })} className={inputCls}>
            <option value="">Semua bintang</option>{[5, 4, 3, 2, 1].map((b) => <option key={b} value={b}>{b} bintang</option>)}
          </select>
          <select data-testid="masukan-jenis" value={f.jenis} onChange={(e) => setF({ ...f, jenis: e.target.value })} className={inputCls}>
            <option value="">Semua jenis</option>{Object.entries(JENIS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select data-testid="masukan-cs" value={f.cs} onChange={(e) => setF({ ...f, cs: e.target.value })} className={inputCls}>
            <option value="">Semua Kasir/CS</option>{(d?.per_cs || []).filter((c) => c.cs_id !== "-").map((c) => <option key={c.cs_id} value={c.cs_id}>{c.cs_nama}</option>)}
          </select>
          <button data-testid="masukan-refresh" onClick={load} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Muat ulang</button>
        </div>
        {error && <div data-testid="masukan-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}
        {!d ? <div className="text-sm text-slate-400"><Loader2 className="inline animate-spin" size={16} /> Memuat…</div> : (
          <>
            <section data-testid="masukan-ringkasan" className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="text-xs text-slate-400">Rata-rata ({d.jumlah} penilaian)</div>
                <div data-testid="masukan-rata" className="text-2xl font-bold text-soq-biru-tua">{rataTeks(d.rata_rata)}</div>
                <div className="mt-1 text-xs text-slate-500">{[5, 4, 3, 2, 1].map((b) => `${b}★ ${d.per_bintang[b]}`).join(" · ")}</div>
              </div>
              <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="mb-1 text-xs text-slate-400">Per bulan</div>
                <table data-testid="masukan-per-bulan" className="w-full text-sm"><tbody>{d.per_bulan.map((b) => (
                  <tr key={b.bulan}><td>{b.bulan}</td><td className="text-right text-slate-400">{b.jumlah}</td><td className="text-right font-semibold">{rataTeks(b.rata_rata)}</td></tr>))}</tbody></table>
              </div>
              <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="mb-1 text-xs text-slate-400">Per Kasir/CS</div>
                <table data-testid="masukan-per-cs" className="w-full text-sm"><tbody>{d.per_cs.map((c) => (
                  <tr key={c.cs_id}><td>{c.cs_nama}</td><td className="text-right text-slate-400">{c.jumlah}</td><td className="text-right font-semibold">{rataTeks(c.rata_rata)}</td></tr>))}</tbody></table>
              </div>
            </section>
            <section className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/60">
              <table data-testid="masukan-table" className="w-full text-sm">
                <thead><tr className="border-b border-slate-800 text-left text-xs text-slate-500">
                  <th className="px-3 py-2">Waktu</th><th className="px-3 py-2">Faktur</th><th className="px-3 py-2">Customer</th><th className="px-3 py-2">Kasir/CS</th>
                  <th className="px-3 py-2">Bintang</th><th className="px-3 py-2">Catatan</th></tr></thead>
                <tbody>
                  {d.rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">Belum ada penilaian.</td></tr>}
                  {d.rows.map((r) => (
                    <tr key={r.id} data-testid={`masukan-row-${r.nomor_faktur}`} className="border-b border-slate-800/60 align-top">
                      <td className="px-3 py-2 text-xs text-slate-400">{W.waktuSedang(r.waktu)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.nomor_faktur}<div className="text-[11px] text-slate-500">{JENIS[r.jenis_transaksi] || r.jenis_transaksi}</div></td>
                      <td className="px-3 py-2">{r.customer}</td>
                      <td className="px-3 py-2 text-xs">{r.cs_nama}</td>
                      <td className="px-3 py-2"><Bintang nilai={r.bintang} />{r.tanda.length > 0 && <div className="mt-1 text-[11px] text-slate-500">{r.tanda.join(" · ")}</div>}</td>
                      <td className="max-w-md px-3 py-2 whitespace-pre-wrap break-words text-slate-300">{r.catatan || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
